import { useEffect, useRef, useState } from "react";
import { cn } from "~/lib/utils";
import { ADSENSE_CLIENT, ADS_ENABLED } from "~/lib/ads";
import { useConsentManager } from "@c15t/react";
import { TRACKING } from "~/lib/consent";
import { isProduction } from "~/lib/env";
import { trackWebEvent } from "~/lib/web-analytics";

// Request the ad only once the slot is within ~2 screens of the viewport, so a filled ad is one the
// user is about to see (fewer wasted requests; keeps "filled" close to "viewable"). WEB-74.
const AD_LAZY_ROOT_MARGIN = "200% 0px";
// Count a viewable impression once the filled ad is at least half on screen.
const AD_VIEWABLE_RATIO = 0.5;
// If AdSense never sets data-ad-status this long after the request, hide the empty slot and report
// ad_timeout (covers a network-level adblocker that silently drops the script). Runs from the request
// so the blank is gone before the user scrolls to it — no on-screen empty cell, no collapse-reflow.
const AD_FILL_TIMEOUT_MS = 10000;

interface AdSlotProps {
  /** Stable, unique id for autocapture analytics, e.g. "deal-feed-ad-1". */
  id: string;
  /** Funnel placement id, e.g. "deal.feed.display". */
  placementId: string;
  /** data-ad-slot from the AdSense console (see AD_SLOTS in ~/lib/ads). */
  slot: string;
  /** data-ad-format: "fluid" (in-feed), "auto" | "vertical" | "rectangle" (display). */
  format?: string;
  /** data-ad-layout-key — in-feed units only. */
  layoutKey?: string;
  fullWidthResponsive?: boolean;
  /** Layout classes from the caller (width belongs to the caller). */
  className?: string;
}

/**
 * One AdSense ad unit. Renders <ins class="adsbygoogle">, requests an ad when the slot nears the
 * viewport, and collapses to nothing when there is no ad to show (unfilled: EEA with no CMP, no fill,
 * blocked region; or a network-level adblocker that keeps the script from ever setting a status), so
 * callers never show an empty hole.
 *
 * Emits the passive-placement funnel to the first-party pipeline: ad_impression once the filled ad is
 * actually viewable (≥50% on screen); ad_no_fill for the expected zero-inventory / EEA-no-CMP case;
 * ad_error for an adblocker that throws on push; ad_timeout when no status arrives in the window
 * (silent network-level block or too-slow fill). Fires only in production (staging/dev render a
 * placeholder with no <ins>). There is no ad_click — AdSense clicks happen in a cross-origin iframe
 * and are not observable from the page (click counts live in the AdSense console).
 */
export function AdSlot({
  id,
  placementId,
  slot,
  format = "auto",
  layoutKey,
  fullWidthResponsive,
  className,
}: AdSlotProps) {
  const insRef = useRef<HTMLModElement>(null);
  const [unfilled, setUnfilled] = useState(false);
  // No consent, no slot. With no loader present a push throws nothing — it appends to a plain array —
  // so the slot would sit out the fill timeout and report ad_timeout, which exists to signal an
  // adblocker. Gating the render is what keeps that metric meaningful.
  const trackingGranted = useConsentManager().has(TRACKING);

  useEffect(() => {
    if (!ADS_ENABLED) return;
    const ins = insRef.current;
    if (!ins) return;

    let filled = false;
    let viewable = false;
    let reported = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const stopWatching = () => {
      statusObserver.disconnect();
      viewObserver.disconnect();
      clearTimeout(timer);
    };
    // Exactly one terminal funnel event per slot.
    const report = (
      eventType: "ad_impression" | "ad_no_fill" | "ad_error" | "ad_timeout",
      extra?: Record<string, unknown>
    ) => {
      if (reported) return;
      reported = true;
      trackWebEvent({
        event_type: eventType,
        placement_id: placementId,
        format,
        ...extra,
      });
      stopWatching();
    };
    // Collapse the slot when no ad was shown, reporting the outcome. A filled ad is never collapsed.
    //  - ad_no_fill: Google returned no ad (expected zero-inventory / EEA-no-CMP).
    //  - ad_error:   the push threw; `reason` carries the exception's name.
    //  - ad_timeout: no status at all within the window (network-level adblock that silently drops
    //                the script, or a fill too slow to matter). Distinct from ad_error because a lazy
    //                off-screen request can't tell "blocked" from "slow"; hidden from the request so
    //                the blank is gone before the user scrolls to it (no on-screen blank, no reflow).
    const collapse = (eventType: "ad_no_fill" | "ad_error" | "ad_timeout", extra?: Record<string, unknown>) => {
      if (filled) return;
      report(eventType, extra);
      setUnfilled(true);
    };
    const maybeImpression = () => {
      if (filled && viewable) report("ad_impression");
    };

    const checkStatus = () => {
      const status = ins.getAttribute("data-ad-status");
      if (status === "filled") {
        if (filled) return;
        filled = true;
        // The <ins> had ~0 area while empty; re-observe so viewability reflects the filled size
        // even if the ad filled in place. The fresh (async) callback recomputes `viewable` and fires
        // the impression — don't read the stale `viewable` synchronously here.
        viewObserver.unobserve(ins);
        viewObserver.observe(ins);
      } else if (status === "unfilled") {
        collapse("ad_no_fill");
      }
    };

    const statusObserver = new MutationObserver(checkStatus);

    const viewObserver = new IntersectionObserver(
      entries => {
        viewable = entries.some(e => e.isIntersecting && e.intersectionRatio >= AD_VIEWABLE_RATIO);
        maybeImpression();
      },
      { threshold: [AD_VIEWABLE_RATIO] }
    );

    const requestAd = () => {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        // What throws is the assignment, not the push: adblockers redefine `window.adsbygoogle` as
        // a getter-only property, and writing to one throws TypeError in strict mode (this file is
        // a module). A missing loader on its own is harmless — the push appends to a plain array
        // and the slot falls through to the fill timeout below.
        collapse("ad_error", { reason: error instanceof Error ? error.name : typeof error });
        return;
      }
      statusObserver.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
      viewObserver.observe(ins);
      timer = setTimeout(() => {
        if (!ins.getAttribute("data-ad-status")) collapse("ad_timeout");
      }, AD_FILL_TIMEOUT_MS);
      // Catch a status already set before the observer attached.
      checkStatus();
    };

    // Lazy-load: fire the request once the slot nears the viewport, then stop watching for that.
    const lazyObserver = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          lazyObserver.disconnect();
          requestAd();
        }
      },
      { rootMargin: AD_LAZY_ROOT_MARGIN }
    );
    lazyObserver.observe(ins);

    return () => {
      lazyObserver.disconnect();
      statusObserver.disconnect();
      viewObserver.disconnect();
      clearTimeout(timer);
    };
  }, [placementId, format, trackingGranted]);

  if (!ADS_ENABLED || unfilled) return null;

  // Only production is an approved AdSense origin; localhost and staging never fill. Everywhere but
  // production, render a labeled placeholder so the slot's position and size are visible while
  // building feed layouts. Sizing (height) comes from the caller's className, like the real <ins>.
  // Consent does not gate this branch: a placeholder renders no <ins> and touches no AdSense.
  if (!isProduction) {
    return (
      <div
        id={id}
        className={cn(
          "border-muted-foreground/40 bg-muted text-muted-foreground flex min-h-24 flex-col items-center justify-center gap-1 border border-dashed text-xs",
          className
        )}
      >
        <span>Ad Placeholder</span>
        <span>slot: {slot}</span>
      </div>
    );
  }

  if (!trackingGranted) return null;

  return (
    <ins
      ref={insRef}
      id={id}
      className={cn("adsbygoogle block", className)}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={slot}
      data-ad-format={format}
      data-ad-layout-key={layoutKey}
      data-full-width-responsive={fullWidthResponsive ? "true" : undefined}
    />
  );
}
